import CloseIcon from "@mui/icons-material/Close";
import { Card, CardContent, Divider, List } from "@mui/material";
import { Map, useMap } from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import FullScreenLayout from "../../layout/FullScreenLayout";
import config from "../../utils/Config";
import {
  fetchRoute,
  fitMapToMarkers,
  getJitteredAddresses,
  hasDistinctDestination,
  IGMRoute,
  INFO_TYPE,
  makeRouteData,
  reorderLocationsBasedOnOptimization,
} from "../../utils/GoogleMapsUtils";
import {
  CalendarMapContainer,
  CalendarMapSidebar,
  CloseModalButton,
  MapContainer,
  Section as CalendarSection,
} from "./../Calendar.style";
import LocationAddressInfo from "./LocationAddressInfo";
import LocationAddressMarker from "./LocationAddressMarker";
import LocationInfo from "./LocationInfo";
import LocationMarker from "./LocationMarker";
import MarkerInfoWindow from "./MarkerInfoWindow";
import PolylineOverlay from "./PolylineOverlay";
import ScheduleHeader from "./ScheduleHeader";
import StartEndLocation from "./StartEndLocation";
import { IScheduledVisits } from "@home-health-notify/domain";
import { useSnackbar } from "notistack";
import { debounce } from "hhn-commons";

interface CalendarMapModalProps {
  open: boolean;
  closeModal: () => void;
  routeData: IScheduledVisits[];
}

const CalendarMapModal = ({
  open,
  closeModal,
  routeData,
}: CalendarMapModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const map = useMap("my-calendar-map");
  const [selectedLocation, setSelectedLocation] = useState<
    IScheduledVisits | undefined
  >(undefined);
  const [originLocation, setOriginLocation] = useState<
    google.maps.places.PlaceResult | undefined
  >(undefined);
  const [destinationLocation, setDestinationLocation] = useState<
    google.maps.places.PlaceResult | undefined
  >(undefined);

  const [gmRouteData, setGmRouteData] = useState<IGMRoute>();
  const [infoType, setInfoType] = useState<INFO_TYPE>(INFO_TYPE.DEFAULT);
  const [addresses, setAddresses] = useState<IScheduledVisits[]>(
    makeRouteData(routeData, infoType),
  );
  const [encodedPolyline, setEncodedPolyline] = useState("");

  useEffect(() => {
    setAddresses(makeRouteData(routeData, infoType));
  }, [infoType, routeData]);

  const isOptimize = infoType === INFO_TYPE.SUGGESTED;

  const showDestinationMarker = hasDistinctDestination(
    originLocation?.geometry?.location?.toJSON(),
    destinationLocation?.geometry?.location?.toJSON(),
  );

  const getRoute = useCallback(async () => {
    const addressesLatLng: google.maps.LatLngLiteral[] = addresses.map(r => {
      if (!r.location || !Array.isArray(r.location.coordinates)) {
        return { lat: 0, lng: 0 };
      }
      return {
        lat: r.location.coordinates[1],
        lng: r.location.coordinates[0],
      };
    });
    const route = await fetchRoute({
      locations: addressesLatLng,
      isOptimize,
      originLocation:
        originLocation &&
        originLocation.geometry &&
        originLocation.geometry.location
          ? originLocation.geometry.location.toJSON()
          : undefined,
      destinationLocation:
        destinationLocation &&
        destinationLocation.geometry &&
        destinationLocation.geometry.location
          ? destinationLocation.geometry.location.toJSON()
          : undefined,
    });
    if (route) {
      setGmRouteData(route);
    } else {
      enqueueSnackbar("No routes available");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationLocation, isOptimize, originLocation]);
  console.log(
    "addresses, originLocation, destinationLocation, isOptimize ===>",
    addresses,
    originLocation,
    destinationLocation,
    isOptimize,
  );
  // Only call getRoute when addresses, originLocation, destinationLocation, or isOptimize changes
  const debouncedGetRoute = useMemo(() => debounce(getRoute, 300), [getRoute]);

  useEffect(() => {
    if (addresses.length > 0) {
      debouncedGetRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses, originLocation, destinationLocation, isOptimize]);

  const jitteredMarkers = useMemo(
    () =>
      map && typeof map.getZoom() === "number"
        ? getJitteredAddresses(addresses, map.getZoom() as number)
        : addresses,
    [addresses, map],
  );

  useEffect(() => {
    if (map && jitteredMarkers?.length > 0) {
      const timeout = setTimeout(() => {
        fitMapToMarkers(map, jitteredMarkers);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [map, jitteredMarkers]);

  useEffect(() => {
    if (gmRouteData) {
      if (
        isOptimize &&
        gmRouteData.optimizedIntermediateWaypointIndex &&
        gmRouteData.optimizedIntermediateWaypointIndex.length > 0
      ) {
        const optimizedIndices = gmRouteData.optimizedIntermediateWaypointIndex;
        const reorderedLocations = reorderLocationsBasedOnOptimization(
          addresses,
          optimizedIndices,
        );
        // Only reorder if addresses are not already in optimized order
        const isAlreadyOptimized = reorderedLocations.every(
          (loc, idx) => loc.id === addresses[idx].id,
        );
        if (!isAlreadyOptimized) {
          setAddresses(reorderedLocations);
        }
      }
      setEncodedPolyline(gmRouteData.polyline.encodedPolyline);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmRouteData]);

  const onInfoTypeChange = (event: React.SyntheticEvent, newValue: number) => {
    setInfoType(newValue);
  };

  return (
    open && (
      <FullScreenLayout>
        <CalendarMapContainer data-testid="calendar-map">
          <CalendarMapSidebar>
            <CalendarSection>
              <Card elevation={0} sx={{ mb: 3 }}>
                <ScheduleHeader
                  addresses={addresses}
                  value={infoType}
                  onChange={onInfoTypeChange}
                />
                <Divider />
                <StartEndLocation
                  onRouteChange={route => {
                    setOriginLocation(route.start ?? undefined);
                    if (!route.end)
                      setDestinationLocation(route.start ?? undefined);
                    if (route.end)
                      setDestinationLocation(route.end ?? undefined);
                  }}
                />
                <CardContent sx={{ p: 0 }}>
                  <List disablePadding>
                    {originLocation && (
                      <LocationAddressInfo
                        location={originLocation}
                        endpoint="start"
                      />
                    )}
                    {addresses.map((location, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && <Divider />}
                        <LocationInfo
                          onClick={() => setSelectedLocation(location)}
                          index={index}
                          location={location}
                          isSelected={selectedLocation?.id === location.id}
                        />
                      </React.Fragment>
                    ))}
                    {destinationLocation && (
                      <LocationAddressInfo
                        location={destinationLocation}
                        endpoint="end"
                      />
                    )}
                  </List>
                </CardContent>
              </Card>
            </CalendarSection>
          </CalendarMapSidebar>
          <MapContainer>
            <Map
              id="my-calendar-map"
              defaultCenter={{ lat: 40.73061, lng: -73.935242 }}
              defaultZoom={12}
              style={{ width: "100%", height: "100%" }}
              mapId={config.googleMapId}
              fullscreenControl={false}
              mapTypeControl={false}
              zoomControl={false}
              scrollwheel={false}
            >
              {encodedPolyline && (
                <PolylineOverlay encodedPath={encodedPolyline} />
              )}

              {originLocation && (
                <LocationAddressMarker
                  location={originLocation}
                  endpoint="start"
                />
              )}
              {showDestinationMarker && destinationLocation && (
                <LocationAddressMarker
                  location={destinationLocation}
                  endpoint="end"
                />
              )}
              {jitteredMarkers.length > 0 &&
                jitteredMarkers.map((address, i) => {
                  return (
                    <LocationMarker
                      key={address.id}
                      address={address}
                      onClick={() => setSelectedLocation(address)}
                      text={i + 1 + ""}
                      isSelected={selectedLocation?.id === address.id}
                    />
                  );
                })}
            </Map>
            <CloseModalButton onClick={closeModal}>
              <CloseIcon />
            </CloseModalButton>
          </MapContainer>
          {/* InfoWindow for selected location */}
          {selectedLocation && (
            <MarkerInfoWindow
              selectedLocation={selectedLocation}
              onCloseClick={() => setSelectedLocation(undefined)}
            />
          )}
        </CalendarMapContainer>
      </FullScreenLayout>
    )
  );
};

export default CalendarMapModal;
